/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 *
 * @file OnboardingGetStartedPanel.jsx
 * Action cards row and preview link for the onboarding splash.
 * @author Anuja Gaikwad
 * @since 2.0.0
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@mui/material';
import OnboardingActionCard from './OnboardingActionCard';
import { classes } from './styles/OnboardingSplashStyles';
import { getDataAttributes, dataAttributesPropType } from '../../common/dataAttributes';

const actionCardPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  iconName: PropTypes.string.isRequired,
  badgeLabel: PropTypes.string,
  ctaLabel: PropTypes.string
});

const OnboardingGetStartedPanel = ({
  actionCards,
  onActionClick,
  previewQuestions,
  onPreviewQuestionsClick
}) => (
  <div className={classes.cardsColumn}>
    <div className={classes.actionCardList}>
      {actionCards.map((card) => (
        <OnboardingActionCard
          key={card.id}
          actionId={card.id}
          title={card.title}
          description={card.description}
          iconName={card.iconName}
          badgeLabel={card.badgeLabel}
          ctaLabel={card.ctaLabel}
          onClick={onActionClick}
          dataAttributes={card.dataAttributes}
          ctaDataAttributes={card.ctaDataAttributes}
        />
      ))}
    </div>

    {previewQuestions?.linkLabel && (
      <Typography component="p" className={classes.previewQuestionsText}>
        {previewQuestions.promptLabel && `${previewQuestions.promptLabel} `}
        <button
          type="button"
          className={classes.previewQuestionsLink}
          onClick={onPreviewQuestionsClick}
          {...getDataAttributes(previewQuestions.dataAttributes)}
        >
          {previewQuestions.linkLabel}
        </button>
      </Typography>
    )}
  </div>
);

OnboardingGetStartedPanel.propTypes = {
  actionCards: PropTypes.arrayOf(actionCardPropType).isRequired,
  onActionClick: PropTypes.func,
  previewQuestions: PropTypes.shape({
    promptLabel: PropTypes.string,
    linkLabel: PropTypes.string.isRequired,
    dataAttributes: dataAttributesPropType
  }),
  onPreviewQuestionsClick: PropTypes.func
};

OnboardingGetStartedPanel.defaultProps = {
  onActionClick: () => {},
  previewQuestions: null,
  onPreviewQuestionsClick: () => {}
};

export default OnboardingGetStartedPanel;
